import React, { useCallback, useState } from 'react';
import {
  FaGripVertical,
  FaExclamationCircle,
  FaFlag,
  FaInfoCircle,
  FaCheckSquare,
  FaTrash,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { CrmTask } from '../../sales/services/CrmTaskService';
import PlantafelService, { BacklogMeta } from '../services/PlantafelService';
import styles from './PlantafelLeftPanel.module.css';

interface LeftPanelBacklogProps {
  tasks: CrmTask[];
  meta: BacklogMeta | null;
  onTaskClick: (task: CrmTask) => void;
  onRefresh?: () => Promise<void>;
}

const isOverdue = (dueDate: string | null): boolean => {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return due < today;
};

const formatDueDate = (dueDate: string | null): string => {
  if (!dueDate) return '';
  const date = new Date(dueDate);
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
};

export function LeftPanelBacklog({
  tasks,
  meta,
  onTaskClick,
  onRefresh,
}: LeftPanelBacklogProps): React.JSX.Element {
  const [selectedJobIds, setSelectedJobIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleJobClick = useCallback((task: CrmTask, e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      setSelectedJobIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(task.id)) {
          newSet.delete(task.id);
        } else {
          newSet.add(task.id);
        }
        return newSet;
      });
    } else {
      setSelectedJobIds(new Set());
      onTaskClick(task);
    }
  }, [onTaskClick]);

  const handleSelectAllJobs = useCallback(() => {
    if (selectedJobIds.size === tasks.length) {
      setSelectedJobIds(new Set());
    } else {
      setSelectedJobIds(new Set(tasks.map(t => t.id)));
    }
  }, [tasks, selectedJobIds.size]);

  const handleBulkDeleteJobs = useCallback(async () => {
    if (selectedJobIds.size === 0) return;

    const confirmed = window.confirm(`${selectedJobIds.size} Job(s) wirklich löschen?\n\nAlle Daten werden unwiderruflich gelöscht!`);
    if (!confirmed) return;

    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of Array.from(selectedJobIds)) {
      try {
        await PlantafelService.deleteJob(id);
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to delete job ${id}:`, err);
      }
    }

    setBulkDeleting(false);
    setSelectedJobIds(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} Job(s) gelöscht`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} Job(s) konnten nicht gelöscht werden`);
    }

    if (onRefresh) {
      await onRefresh();
    }
  }, [selectedJobIds, onRefresh]);

  return (
    <>
      <div className={styles.backlogHeader}>
        <div className={styles.backlogHeaderRow}>
          <div className={styles.backlogCount}>
            {tasks.length} ungeplante Jobs
          </div>
          {tasks.length > 0 && (
            <button
              onClick={handleSelectAllJobs}
              title={selectedJobIds.size === tasks.length ? 'Auswahl aufheben' : 'Alle auswählen (oder Cmd/Ctrl+Click)'}
              className={`${styles.selectAllButton} ${selectedJobIds.size > 0 ? styles.selectAllButtonActive : ''}`}
            >
              <FaCheckSquare size={10} />
            </button>
          )}
        </div>

        {selectedJobIds.size > 0 && (
          <div className={styles.bulkActionBar}>
            <div className={styles.bulkActionCount}>
              {selectedJobIds.size} ausgewählt
            </div>
            <button
              onClick={handleBulkDeleteJobs}
              disabled={bulkDeleting}
              className={`${styles.bulkDeleteButton} ${bulkDeleting ? styles.bulkDeleteButtonDisabled : ''}`}
            >
              <FaTrash size={10} />
              {bulkDeleting ? 'Lösche...' : 'Löschen'}
            </button>
          </div>
        )}
      </div>

      <div className={styles.backlogList}>
        {tasks.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateMessage}>Keine ungeplanten Tasks</div>

            {meta && (
              <div className={styles.metaBox}>
                <div className={styles.metaBoxTitle}>
                  <FaInfoCircle size={12} />
                  Warum leer?
                </div>
                <div className={styles.metaBoxDetails}>
                  {meta.excluded_because_scheduled > 0 && (
                    <div>
                      <span className={styles.metaValue}>{meta.excluded_because_scheduled}</span> bereits
                      eingeplant
                    </div>
                  )}
                  {meta.excluded_because_no_project > 0 && (
                    <div>
                      <span className={styles.metaValue}>{meta.excluded_because_no_project}</span> ohne Projekt
                    </div>
                  )}
                  {meta.excluded_because_done > 0 && (
                    <div>
                      <span className={styles.metaValue}>{meta.excluded_because_done}</span> erledigt
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          tasks.map((task) => {
            const priorityColors = PlantafelService.getPriorityColor(task.priority);
            const overdue = isOverdue(task.due_date);
            const isSelected = selectedJobIds.has(task.id);

            const cardClass = [
              styles.taskCard,
              overdue && !isSelected ? styles.taskCardOverdue : '',
              isSelected ? styles.taskCardSelected : '',
            ].filter(Boolean).join(' ');

            return (
              <div
                key={task.id}
                data-testid={`backlog-task-${task.id}`}
                draggable
                onDragStart={(e) => {
                  console.log('[DRAG DEBUG] DragStart:', { taskId: task.id, taskTitle: task.title });
                  e.dataTransfer.setData('taskId', task.id.toString());
                  e.dataTransfer.setData('taskTitle', task.title);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={(e) => handleJobClick(task, e)}
                className={cardClass}
              >
                <div className={styles.taskCardInner}>
                  <div className={styles.gripIcon}>
                    <FaGripVertical size={12} />
                  </div>
                  <div className={styles.taskCardBody}>
                    <div className={styles.taskTitle}>
                      {task.title}
                    </div>
                    <div className={styles.taskMeta}>
                      {(task.partner_name || task.project_name) && (
                        <span className={styles.taskPartner}>
                          {[task.partner_name, task.project_name].filter(Boolean).join(' – ')}
                        </span>
                      )}
                      {task.due_date && (
                        <span className={`${styles.taskDueDate} ${overdue ? styles.taskDueDateOverdue : ''}`}>
                          {overdue && <FaExclamationCircle size={10} />}
                          {formatDueDate(task.due_date)}
                        </span>
                      )}
                    </div>
                    <div className={styles.taskTags}>
                      <span
                        className={styles.priorityBadge}
                        style={{
                          background: priorityColors.bg,
                          color: priorityColors.text,
                        }}
                      >
                        <FaFlag size={8} />
                        {task.priority}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
